import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import { ILoggedInUser, useAuth } from "../auth/auth-context";
import { AccountManagementViewModel } from "./account-management-view-model";
import ActionPanel from "../action-panel/action-panel";
import { TProductPlan } from "src/components/molecules/product-plan-marker/product-plan-marker";
import Logo from "src/components/molecules/logo/Logo";
import DropdownDefaultBox from "src/components/atoms/dropdown/dropdown-default";
import Alert from "src/components/atoms/alert/alert";
import LogoutLinkButton from "src/components/molecules/logout-button/logout-link-button";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { ErrorDisplay } from "src/components/atoms/error/error";
import { refreshTokens } from "../auth";
import login from "../auth/login";
import { PermissionBagService } from "../auth/permissionBagService";
import { AccountViewModel } from "../account-selection/account-selection-view-model";
import { TokenHelper } from "../auth/token-helper";

const CreateAccount = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [creatingAccount, setCreatingAccount] = useState(false);
	const viewModel = useRef<AccountManagementViewModel | null>(null);
	const [accountCreator, setAccountCreator] = useState<ILoggedInUser>();
	const [accountName, setAccountName] = useState("");
	const [productPlan, setProductPlan] = useState<TProductPlan>("Basic");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const accountViewModel = useRef<AccountViewModel | null>(null);
	const tokenHelper = useRef<TokenHelper | null>(null);

	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new AccountManagementViewModel(
				user.accessToken
			);
			setAccountCreator(user);
		} else {
			navigate("/login");
		}
	}, []);

	const refreshSessionAsync = async (accountId: string): Promise<boolean> => {
		if (accountViewModel.current) {
			const result =
				await accountViewModel.current.setSessionAsync(accountId);

			if (result) {
				try {
					await new Promise((resolve, reject) => {
						refreshTokens(async (err, tokenResult) => {
							if (tokenResult) {
								try {
									const decodedIdToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.idToken
										);
									const decodedAccessToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.accessToken
										);

									if (decodedAccessToken) {
										const permissionService =
											new PermissionBagService(
												tokenResult.accessToken
											);
										const permissions =
											await permissionService.getPermissionBag(
												decodedAccessToken?.role ?? ""
											);

										const loggedInUser: ILoggedInUser = {
											id: decodedAccessToken.sub,
											givenName:
												decodedAccessToken.given_name,
											familyName:
												decodedAccessToken.family_name,
											username: decodedAccessToken.email,
											phoneNumber: `${decodedIdToken?.phone_number ?? 0}`,
											ex_user_id:
												decodedAccessToken.username,
											accessToken:
												tokenResult.accessToken,
											accountId:
												decodedAccessToken.account_id ??
												"",
											email:
												decodedAccessToken.email ?? "",
											role: decodedAccessToken.role,
											exp: decodedAccessToken.exp,
											reseller_id:
												decodedAccessToken.reseller_id,
											permissionBag: permissions
												? permissions
												: { permissions: [] },
											has_multiple_accounts:
												decodedAccessToken.has_multiple_accounts,
										};

										login(loggedInUser); // Directly log in the user
										setLoading(false);
										resolve(true); // Resolve the promise to indicate success
									} else {
										setLoading(false);
										navigate("/error");
										reject(
											new Error(
												"Access token decoding failed"
											)
										);
									}
								} catch (decodeError) {
									console.error(
										"Error decoding tokens:",
										decodeError
									);
									reject(decodeError);
								}
							} else {
								setLoading(false);
								console.error("Failed to refresh tokens:", err);
								reject(err);
							}
						});
					});
					return true; // Return true if everything succeeds
				} catch (error) {
					console.error("Error in refreshSessionAsync:", error);
					return false; // Return false if an error occurs
				}
			}
		}
		return false; // Return false if accountViewModel.current is null or setSessionAsync fails
	};

	const handleCreateAccount = async () => {
		setError("");
		if (user?.reseller_id) {
			if (accountCreator) {
				setCreatingAccount(true);
				try {
					const accountCreated =
						await viewModel.current?.createAccountAsync(
							user.reseller_id,
							{
								name: accountName,
								rootEmailId: accountCreator.email,
								isMultiBusiness: true,
								productPlan: productPlan,
							}
						);

					if (accountCreated) {
						// await refreshSessionAsync(accountCreated.)
						navigate("/select-account");
					} else {
						setError(
							"Unable to create the account, please try later or contact support"
						);
					}
				} catch (error) {
					console.log(error);
					setError(
						"Unable to create the account, please try later or contact support"
					);
				} finally {
					setCreatingAccount(false);
				}
			}
		}
	};

	return (
		<>
			{/* main */}
			<div className="bg-gradient-to-r from-gray-700 to-gray-900 flex flex-col items-center justify-center w-screen h-screen px-6">
				<div className="bg-white flex flex-col items-end justify-center p-6 drop-shadow-2xl">
					<LogoutLinkButton></LogoutLinkButton>
					<div className="flex flex-col items-center justify-center gap-4 w-full">
						<Logo></Logo>
						<span className="font-Poppins font-semibold text-xl text-center">
							Create new Secured calls account
						</span>
					</div>
					<div className="flex flex-col items-center justify-center gap-6 w-[600px] p-4">
						<div className="flex flex-col items-center justify-center gap-1 w-full">
							<span className="font-Poppins font-semibold text-xl text-center">
								Account owner
							</span>
							<span className="font-Poppins font-semibold text-md text-center">
								{accountCreator?.givenName}{" "}
								{accountCreator?.familyName}
							</span>
							<span className="font-Poppins font-semibold text-sc text-center">
								{accountCreator?.email}
							</span>

							<Alert
								text={
									"You can change the account owner from the Account menu later"
								}
								type={"info"}
							></Alert>
						</div>
						<InputBox
							label={"Account name"}
							type={"text"}
							value={accountName}
							onChange={function (value: string): void {
								setAccountName(value);
							}}
						></InputBox>
						<DropdownDefaultBox
							defaultText={"Basic"}
							options={[
								{
									label: "Basic",
									value: "Basic",
								},
								{
									label: "Advanced",
									value: "Advanced",
								},
							]}
							onSelected={(selectedOption): void => {
								setProductPlan(
									selectedOption.value as TProductPlan
								);
							}}
						></DropdownDefaultBox>
						<ErrorDisplay error={error}></ErrorDisplay>
						<ActionPanel
							primaryButtonLabel={"Create"}
							primaryButtonEnabled={
								accountCreator != undefined &&
								accountName != "" &&
								user != null &&
								user.reseller_id != undefined
							}
							secondaryButtonLabel={"Back"}
							secondaryButtonEnabled={true}
							primaryButtonAction={handleCreateAccount}
							secondaryButtonAction={() =>
								navigate("/select-account")
							}
						></ActionPanel>
					</div>
				</div>
				<LoadingModal open={creatingAccount}></LoadingModal>
			</div>
		</>
	);
};

export default CreateAccount;
